import { ChangeDetectorRef, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { WebinarService } from 'src/app/services/shared/webinar.service';
import Swal from 'sweetalert2';
import { GeographicalChip, CustomChip } from 'src/app/models/product-model';
import { GeographicalType } from 'src/app/enums/product-enum';

@Component({
  selector: 'app-webinar-filter',
  templateUrl: './webinar-filter.component.html',
  styleUrls: ['./webinar-filter.component.scss']
})
export class WebinarFilterComponent implements OnInit {
  webinarFilterForm!: FormGroup;
  speakersList: string[] = [];
  organizationsList: string[] = [];
  companiesList: string[] = [];
  creatorsList: string[] = [];
  allCreatorList: any[] = [];
  
  chips: GeographicalChip[] = [];
  speakerControl = new FormControl();
  orgControl = new FormControl();
  companyControl = new FormControl();
  createdByControl = new FormControl();

  filteredSpeakers: string[] = [];
  filteredOrganizations: string[] = [];
  filteredCompanies: string[] = [];
  filteredCreators: string[] = [];

  selectedSpeakers: string[] = [];
  selectedOrganizations: string[] = [];
  selectedCompanies: string[] = [];
  selectedCreators: { id: string; name: string }[] = [];
  selectedTypes: string[] = [];

  @Output() clearFilterData = new EventEmitter<void>();

  GeographicalType = GeographicalType;

  webinarStatus = [
    { label: 'Upcoming', value: 'upcoming' },
    { label: 'Archived', value: 'archived' }
  ];

  webinarType = [
    {label: 'CME', value: 'cme'},
    {label: 'Webinar', value: 'webinar'}
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<any>,
    private fb: FormBuilder,
    private webinarService: WebinarService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.loadInitialData();
    this.loadOrganizations();
    this.loadCompanies(); 
    this.loadCreators();

    if (this.data?.filterData) {
      this.applyExistingFilters();
    }

    this.filteredSpeakers = [...this.speakersList];
    this.filteredOrganizations = [...this.organizationsList];
    this.filteredCompanies = [...this.companiesList]; 
    this.filteredCreators = [...this.creatorsList];
    this.cdr.detectChanges();

    this.speakerControl.valueChanges.subscribe(value => {
      const filterValue = (value && typeof value === 'string') ? value.toLowerCase() : '';
      this.filteredSpeakers = this.speakersList.filter(speaker =>
        speaker.toLowerCase().includes(filterValue)
      );
    });

    this.orgControl.valueChanges.subscribe(value => {
      const filterValue = (value && typeof value === 'string') ? value.toLowerCase() : '';
      this.filteredOrganizations = this.organizationsList.filter(org =>
        org.toLowerCase().includes(filterValue)
      );
    });
    this.companyControl.valueChanges.subscribe(value => {
      const filterValue = (value && typeof value === 'string') ? value.toLowerCase() : '';
      this.filteredCompanies = this.companiesList.filter(company =>
        company.toLowerCase().includes(filterValue)
      );
    });

    this.createdByControl.valueChanges.subscribe(value => {
      const filterValue = (value && typeof value === 'string') ? value.toLowerCase() : '';
      this.filteredCreators = this.creatorsList.filter(creator =>
        creator.toLowerCase().includes(filterValue)
      );
    });

  }
  applyExistingFilters() {
    const filterData = this.data.filterData;

    if (filterData.startDate) {
      try {
        let startDate = filterData.startDate;
        if (typeof startDate === 'string') {
          startDate = new Date(startDate);
        }
        this.webinarFilterForm.get('startDate')?.setValue(startDate);
      } catch (error) {
        console.error('Error setting start date:', error);
      }
    }

    if (filterData.endDate) {
      try {
        let endDate = filterData.endDate;
        if (typeof endDate === 'string') {
          endDate = new Date(endDate);
        }
        this.webinarFilterForm.get('endDate')?.setValue(endDate);
      } catch (error) {
        console.error('Error setting end date:', error);
      }
    }

    if (filterData.speakersName && Array.isArray(filterData.speakersName) && filterData.speakersName.length > 0) {
      this.selectedSpeakers = [...filterData.speakersName];
      this.selectedSpeakers.forEach(speaker => {
        this.addSpeakerChip(speaker);
      });
    }

    if (filterData.orgName && Array.isArray(filterData.orgName) && filterData.orgName.length > 0) {
      this.selectedOrganizations = [...filterData.orgName];
      this.selectedOrganizations.forEach(org => {
        this.addOrganizationChip(org);
      });
    }
    if (filterData.companyName && Array.isArray(filterData.companyName) && filterData.companyName.length > 0) {
      this.selectedCompanies = [...filterData.companyName];
      this.selectedCompanies.forEach(company => {
        this.addCompanyChip(company);
      });
    }
    
    filterData.createdBy.forEach((creatorId: string) => {
      const creatorObj = this.allCreatorList.find(creator => creator.id === creatorId);
      if (creatorObj) {
        this.selectedCreators.push({ id: creatorId, name: creatorObj.name });
        this.addCreatedByChip(creatorObj.name);
      } else {
        this.selectedCreators.push({ id: creatorId, name: creatorId });
        this.addCreatedByChip(creatorId);
      }
    });
  

    if (filterData.status !== null && filterData.status !== undefined) {
      const statusLabel = this.webinarStatus.find(s => s.value === filterData.status)?.label || '';
      this.webinarFilterForm.get('status')?.setValue(statusLabel);
      this.handleStatusChange(statusLabel);
    }

    if(filterData.type !== null && filterData.type !== undefined) {
      const typeLabel = this.webinarType.find(s => s.value === filterData.type)?.label || '';
      this.webinarFilterForm.get('type')?.setValue(typeLabel);
      this.handleTypeChange(typeLabel);
    }

    if (this.data.chips && Array.isArray(this.data.chips) && this.data.chips.length > 0) {
      this.chips = [...this.data.chips];
    }

  }
  loadInitialData() {
    this.webinarService.getSpeakers().subscribe((res: any) => {
      const allSpeakers = res?.data?.map((speaker: any) => speaker.speakersName) || [];
      this.speakersList = Array.from(new Set(allSpeakers));
      this.filteredSpeakers = this.speakersList;
    });
  }

  loadOrganizations() {
    this.webinarService.getSponsors().subscribe((res: any) => {
      const organizations = res?.data || [];
      this.organizationsList = Array.from(
        new Set(organizations.map((org: any) => org.orgName).filter((org: any) => !!org))
      );
      this.filteredOrganizations = this.organizationsList;
    });
  }
  loadCompanies(): Promise<void> {
    return new Promise((resolve) => {
      this.webinarService.getCompanyNames().subscribe({
        next: (res: any) => {
          
          if (res?.data) {
            if (Array.isArray(res.data) && typeof res.data[0] === 'string') {
              this.companiesList = [...res.data].filter(name => !!name);
            } else {
              const companies = res.data;
              const companyNames = companies.map((company: any) => {
                return company.name || company.companyName || company.company || '';
              }).filter((name: string) => !!name);
              
              this.companiesList = Array.from(new Set(companyNames));
            }
            
            this.filteredCompanies = [...this.companiesList];            
            
          } else {
            console.warn('Company data structure unexpected:', res);
            this.companiesList = [];
            this.filteredCompanies = [];
          }
          
          this.cdr.detectChanges();
          resolve();
        },
        error: (err) => {
          console.error('Error fetching companies:', err);
          this.companiesList = [];
          this.filteredCompanies = [];
          this.cdr.detectChanges();
          resolve();
        }
      });
    });
  }

  loadCreators() {
    this.webinarService.getCreatedBy().subscribe({
      next: (res: any) => {
        const creators = res?.data || [];
        this.allCreatorList = res?.data || [];
        this.creatorsList = Array.from(
          new Set(creators.map((creator: any) => creator.name).filter((creator: any) => !!creator))
        );
        this.filteredCreators = this.creatorsList;
        
        if (this.selectedCreators.length > 0) {
          this.selectedCreators.forEach((creator, index) => {
            if (creator.name === creator.id) { 
              const creatorObj = this.allCreatorList.find(c => c.id === creator.id);
              if (creatorObj) {
                this.selectedCreators[index].name = creatorObj.name;
                
                this.updateCreatedByChip(creator.id, creatorObj.name);
              }
            }
          });
        }
      },
      error: (err) => {
        console.error('Error fetching creators:', err);
      }
    });
  }
  updateCreatedByChip(oldValue: string, newValue: string) {
    const chipIndex = this.chips.findIndex(chip => 
      chip.type === GeographicalType.CREATED_BY && chip.name === oldValue);
    
    if (chipIndex !== -1) {
      this.chips[chipIndex].name = newValue;
      this.cdr.detectChanges();
    }
  }

  initializeForm() {
    this.webinarFilterForm = this.fb.group({
      startDate: new FormControl(''),
      endDate: new FormControl(''),
      speaker: new FormControl(''),
      orgName: new FormControl(''),
      companyName: new FormControl(''),
      createdBy: new FormControl(''),
      status: new FormControl(''),
      type: new FormControl('')
    });
  }

  onSpeakerSelected(event: any) {
    const speaker = event.option.value;
    if (speaker && !this.selectedSpeakers.includes(speaker)) {
      this.selectedSpeakers.push(speaker);
      this.addSpeakerChip(speaker);
      this.speakerControl.setValue('');      
    }
  }

  onOrganizationSelected(event: any) {
    const org = event.option.value;
    if (org && !this.selectedOrganizations.includes(org)) {
      this.selectedOrganizations.push(org);
      this.addOrganizationChip(org);
      this.orgControl.setValue('');
  
    }
  }

  onCompanyNameSelected(event: any) {
    const company = event.option.value;
    if (company && !this.selectedCompanies.includes(company)) {
      this.selectedCompanies.push(company);
      this.addCompanyChip(company);
      this.companyControl.setValue('');
      this.cdr.detectChanges();
    }
  }

 
  onCreatedBySelected(event: any) {
    const creatorName = event.option.value;
    const creatorObj = this.allCreatorList.find((creator: any) => creator.name === creatorName);
    
    if (creatorObj && !this.selectedCreators.some(creator => creator.id === creatorObj.id)) {
      this.selectedCreators.push({ id: creatorObj.id, name: creatorObj.name });
      this.addCreatedByChip(creatorObj.name);
      this.createdByControl.setValue('');
    }
  }
  addSpeakerChip(speaker: string) {
    const speakerChip: CustomChip = {
      id: 0,
      name: speaker,
      type: GeographicalType.SPEAKER,
      priority: 16,
      isGeographical: false
    };
    this.addNewChip(speakerChip);
  }

  addOrganizationChip(orgName: string) {
    const orgChip: CustomChip = {
      id: 0,
      name: orgName,
      type: GeographicalType.ORGANIZATION,
      priority: 17,
      isGeographical: false
    };
    this.addNewChip(orgChip);
  }

  addCompanyChip(companyName: string) {
    const companyChip: CustomChip = {
      id: 0,
      name: companyName,
      type: GeographicalType.COMPANY_NAME,
      priority: 15,
      isGeographical: false
    };
    this.addNewChip(companyChip);
  }

  addCreatedByChip(creatorName: string) {
    const creatorChip: CustomChip = {
      id: 0,
      name: creatorName,
      type: GeographicalType.CREATED_BY,
      priority: 8,
      isGeographical: false
    };
    this.addNewChip(creatorChip);
  }

  handleStatusChange(status: string) {
    this.chips = this.chips.filter(chip => chip.type !== GeographicalType.STATUS);

    if (status) {
      const statusChip: CustomChip = {
        id: Math.floor(Math.random() * 1000),
        name: status,
        type: GeographicalType.STATUS,
        priority: 7,
        isGeographical: false
      };

      this.addNewChip(statusChip);
    }
  }

  handleTypeChange(type: string) {
    this.chips = this.chips.filter(chip => chip.type !== GeographicalType.TYPE);
    if (type) {
      const typeChip: CustomChip = {
        id: Math.floor(Math.random() * 1000),
        name: type,
        type: GeographicalType.TYPE,
        priority: 18,
        isGeographical: false
      };

      this.addNewChip(typeChip);
    }
  }

 applyFilter() {
    const formValue = this.webinarFilterForm.value;

    const isAllFiltersEmpty =
      !formValue.startDate &&
      !formValue.endDate &&
      !formValue.status &&
      this.selectedSpeakers.length === 0 &&
      this.selectedOrganizations.length === 0 &&
      this.selectedCompanies.length === 0 &&
      this.selectedCreators.length === 0 &&
      !formValue.type;

    if (isAllFiltersEmpty) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Filters',
        text: 'Please select at least one filter!',
        confirmButtonText: 'OK'
      });
      return;
    }
 
  const normalizeDate = (date: Date | null): string | null => {
    if (!date) return null;
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .split('T')[0]; 
  };

    this.chips = this.chips.filter(chip => {
      if (chip.type === GeographicalType.DATE && (!formValue.startDate || !formValue.endDate)) {
        return false;
      }
      if (chip.type === GeographicalType.STATUS && !formValue.status) {
        return false;
      }
      if (chip.type === GeographicalType.SPEAKER && !this.selectedSpeakers.includes(chip.name)) {
        return false;
      }
      if (chip.type === GeographicalType.ORGANIZATION && !this.selectedOrganizations.includes(chip.name)) {
        return false;
      }
      if (chip.type === GeographicalType.COMPANY_NAME && !this.selectedCompanies.includes(chip.name)) {
        return false;
      }
      if (chip.type === GeographicalType.CREATED_BY && !this.selectedCreators.some(c => c.name === chip.name)) {
        return false;
      }
      if (chip.type === GeographicalType.TYPE && !formValue.type) {
        return false;
      }
      return true;
    });

    if (formValue.startDate && formValue.endDate) {
      this.chips = this.chips.filter(chip => chip.type !== GeographicalType.DATE);

      const startFormatted = formValue.startDate.toLocaleDateString();
      const endFormatted = formValue.endDate.toLocaleDateString();

      const dateChip: CustomChip = {
        id: Math.floor(Math.random() * 1000),
        name: `Date Range: ${startFormatted} - ${endFormatted}`,
        type: GeographicalType.DATE,
        priority: 5,
        isGeographical: false
      };
      this.chips.push(dateChip);
    }

    const selectedStatus = this.webinarStatus.find(s => s.label === formValue.status);
    const statusValue = selectedStatus ? selectedStatus.value : null;

    if (formValue.status) {
      this.chips = this.chips.filter(chip => chip.type !== GeographicalType.STATUS);

      const statusChip: CustomChip = {
        id: Math.floor(Math.random() * 1000),
        name: formValue.status,
        type: GeographicalType.STATUS,
        priority: 7,
        isGeographical: false
      };

      this.chips.push(statusChip);
    }
    const selectedType = this.webinarType.find(t => t.label === formValue.type);
    const typeValue = selectedType ? selectedType.value : null; 

    if (formValue.type) {
      this.chips = this.chips.filter(chip => chip.type !== GeographicalType.TYPE);

      const typeChip: CustomChip = {
        id: Math.floor(Math.random() * 1000),
        name: formValue.type,
        type: GeographicalType.TYPE,
        priority: 8,
        isGeographical: false
      };

      this.chips.push(typeChip);
    } 

    const creatorIds = this.selectedCreators.map(creator => creator.id);
    const filterPayload: any = {
    startDate: normalizeDate(formValue.startDate), 
    endDate: normalizeDate(formValue.endDate), 
    speakersName: this.selectedSpeakers,
    orgName: this.selectedOrganizations,
    companyName: this.selectedCompanies,
    createdBy: creatorIds,
    status: statusValue,
    type: typeValue
    };

    this.dialogRef.close({
      filterPayload: filterPayload,
      chips: this.chips
    });
  }

  addDateRangeChip(startDate: Date, endDate: Date) {
    const start = new Date(startDate).toLocaleDateString();
    const end = new Date(endDate).toLocaleDateString();

    const dateChip: CustomChip = {
      id: Math.floor(Math.random() * 1000),
      name: `Date Range: ${start} - ${end}`,
      type: GeographicalType.DATE,
      priority: 5,
      isGeographical: false
    };
    this.addNewChip(dateChip);
  }

  addNewChip(newChip: GeographicalChip) {
    const chipExists = this.chips.some(chip =>
      chip.name === newChip.name && chip.type === newChip.type
    );

    if (!chipExists) {
      this.chips = [...this.chips, newChip];
    }
  }

  removeChip(chip: { name: string; type: GeographicalType }) {
    this.chips = this.chips.filter(c => !(c.name === chip.name && c.type === chip.type));

    switch (chip.type) {
      case GeographicalType.SPEAKER:
        this.selectedSpeakers = this.selectedSpeakers.filter(s => s !== chip.name);
        break;

      case GeographicalType.ORGANIZATION:
        this.selectedOrganizations = this.selectedOrganizations.filter(o => o !== chip.name);
        break;
      case GeographicalType.COMPANY_NAME:
        this.selectedCompanies = this.selectedCompanies.filter(c => c !== chip.name);
        break;

      case GeographicalType.CREATED_BY:
        this.selectedCreators = this.selectedCreators.filter(c => c.name !== chip.name);
        break;

      case GeographicalType.STATUS:
        this.webinarFilterForm.patchValue({ status: '' });
        break;
      case GeographicalType.DATE:
        this.clearDateRange();
        break; 
      case GeographicalType.TYPE:
        this.webinarFilterForm.patchValue({ type: '' });
        break;
        default:
          break; 
    }
    this.webinarFilterForm.patchValue({
    companyName: this.selectedCompanies.length > 0 ? this.selectedCompanies : '',
    speaker: this.selectedSpeakers.length > 0 ? this.selectedSpeakers : '',
    orgName: this.selectedOrganizations.length > 0 ? this.selectedOrganizations : '',
    createdBy: this.selectedCreators.length > 0 ? this.selectedCreators.map(c => c.id) : ''
  });

  this.cdr.detectChanges();
  }

  closeDialog() {
    this.clearFilter();
    this.clearFilterData.emit();
    this.dialogRef.close(null);
  }

  clearDateRange() {
    this.webinarFilterForm.get('startDate')?.setValue('');
    this.webinarFilterForm.get('endDate')?.setValue('');
  }

  clearFilter() {
    this.webinarFilterForm.reset();
    this.speakerControl.setValue('');
    this.orgControl.setValue('');
    this.chips = [];
    this.selectedSpeakers = [];
    this.selectedOrganizations = [];
    this.selectedCompanies = [];
    this.selectedCreators = [];
    this.clearFilterData.emit();
  }

  displayFn(value: string): string {
    return value || '';
  }

  hasActiveFilters(): boolean {
    const formValue = this.webinarFilterForm.value;
    return (
      !!formValue.startDate ||
      !!formValue.endDate ||
      this.selectedSpeakers.length > 0 ||
      this.selectedOrganizations.length > 0 ||
      this.selectedCompanies.length > 0 ||
      this.selectedCreators.length > 0 ||
      !!formValue.status ||
      !!formValue.type
    );
  }
}